import { cn } from '@helpers/styleUtils';
import PropTypes from 'prop-types';
import ChatBotIcon from '@assets/chat-icon.png';
import { userManager } from '@providers/userManager';
import { Avatar, AvatarFallback, AvatarImage } from '@components/common/Avataar';
import { LoadingDots } from './LoadingDots';
import parse from 'html-react-parser';
import { ThumbsUp, ThumbsDown, MessageCircle } from 'lucide-react';
import useChatbotStore from '@stores/chatbotStore';
import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@components/common/Dialog';
import { Button } from '@components/common/Button';
import { Textarea } from '@components/common/Textarea';
import useNotificationStore from '@stores/notificationStore';
export const ChatBubble = ({ sender, message, isLoading, messageId = null }) => {
    const { addNotification } = useNotificationStore();

    const { postChatFeedback } = useChatbotStore();
    const isUser = sender === 'user';
    const userGData = userManager.getGUserData();
    const user = userManager.getUserData();
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [lastFlag, setLastFlag] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');

    const isInitialMessage = message.includes('Hello! How can I assist you today?');

    const containerClasses = cn(
        'flex w-full mb-6',
        isUser ? 'justify-start items-end flex-row-reverse' : 'items-start justify-start ',
    );

    const bubbleClasses = cn(
        'relative flex flex-col max-w-[80%] rounded-lg px-4 py-3 pb-4 text-sm border hyphens-manual text-wrap whitespace-pre-line',
        isUser ? 'mr-2 bg-primary-blue text-white' : 'ml-2 bg-white text-gray-800',
    );

    const formattedMessage =
        message &&
        message
            .replace(/\*\*(.*?)\*\*/g, '<strong class="contents">$1</strong>')
            .replace(/<a /g, '<a target="_blank" class="text-primary-blue font-bold" ');

    const handleFeedback = (feedback, flag, messageId) => {
        setLastFlag(flag);
        postChatFeedback(feedback, messageId, flag);

        addNotification({
            type: 'success',
            title: 'Chatbot Feedback Submitted',
            message: `Thank you for your feedback!`,
            timeOut: 0,
        });
    };

    const handleFeedbackMessage = messageId => {
        postChatFeedback(feedbackText, messageId, lastFlag);
        setFeedbackText('');
        setShowFeedbackModal(false);
    };

    return (
        <>
            <div className={containerClasses}>
                <div className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center overflow-hidden relative">
                    {!isUser && (
                        <img src={ChatBotIcon} alt="ChatBotIcon" className="h-6 object-contain" />
                    )}
                    {isUser && (
                        <Avatar>
                            <AvatarImage src={userGData.picture} />
                            <AvatarFallback>{user.displayName.charAt(0)}</AvatarFallback>
                        </Avatar>
                    )}
                </div>
                <div className={bubbleClasses}>
                    {isLoading ? (
                        <LoadingDots />
                    ) : (
                        <>
                            {parse(formattedMessage)}
                            {!isUser && !isInitialMessage && (
                                <div className="absolute -bottom-5 left-2 flex gap-2 bg-white px-3 py-0.5 rounded-xl border border-gray-300">
                                    <button
                                        onClick={() => handleFeedback(null, 1, messageId)}
                                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Helpful"
                                    >
                                        <ThumbsUp className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <button
                                        onClick={() => handleFeedback(null, 2, messageId)}
                                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Not helpful"
                                    >
                                        <ThumbsDown className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <button
                                        onClick={() => setShowFeedbackModal(true)}
                                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Give feedback"
                                    >
                                        <MessageCircle className="w-4 h-4 text-gray-500" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
                <DialogContent className="sm:max-w-[425px] h-[270px]">
                    <DialogHeader>
                        <DialogTitle>Provide Feedback</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <Textarea
                            placeholder="What feedback would you like to share?"
                            value={feedbackText}
                            onChange={e => setFeedbackText(e.target.value)}
                            className="min-h-[100px]"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="bg-primary-blue text-white"
                            onClick={() => handleFeedbackMessage(messageId)}
                            disabled={!feedbackText.trim()}
                        >
                            Submit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

ChatBubble.propTypes = {
    sender: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    messageId: PropTypes.string,
};
